import { Engine } from 'json-rules-engine';
import React from 'react';
import { checkIfObjectIsEmpty, convertDateToString, distinct, isGenentechPharmacyControlIncomplete, stripNonNumeric } from '../Helpers/Calculations';
import { States } from './Constants';
import { allTabs } from './Tabber';

import { cleanNonSpaceWhiteSpace } from '../Helpers/Helpers';
import { genentechHubPharmacyDictionary } from './controls/GenentechHubPharmacyDictionary';
import { getDayjs } from '../Helpers/TimeFunctions';

export const FormContext = React.createContext({});

const dataMissing = (data, state, def) => {
    let myData = data;

    //do we need 'checkboxlist' too?
    //If the data is not included in the dropdown, then it should be missing.
    if (def.type === 'select' || def.type === 'dropdown' || def.type === 'radio' || def.type === 'radio-vertical' || def.type === 'radiogrouplist') {
        if (!def.values.includes(myData)) myData = null;
    }

    if (def.type === "states" || def.type === "usstates") {
        if (!States.map(s => s.Value).includes(myData)) myData = null;
    }

    if (def.type === 'genentechHubPharmacy') {
        if (!data) return false;
        const myData = JSON.parse(data);
        return isGenentechPharmacyControlIncomplete(myData, genentechHubPharmacyDictionary);
    }

    return state.required &&
        state.hidden !== true &&
        (myData === '' || myData === null || myData === undefined || (Array.isArray(data) && !myData.length));
}


//ugh this constructor needs to be fixed -- too many params
class FormEngine {
    constructor(defs, layout, rules, actions, initData, autofill, meta, gtr, ptr, applicationDetails, overrides) {
        this.gtr = gtr;
        this.ptr = ptr;
        this.meta = meta || {};
        this.fieldDefs = defs || [];
        this.layout = layout || [];
        this.autofill = autofill || {};
        this.initData = initData || {};
        this.applicationDetails = applicationDetails || {};
        this.overrides = overrides || {};

        this.fieldState = this.__initFieldState(this.fieldDefs);
        this.formData = this.__initFormData(this.fieldDefs, this.initData, this.autofill, this.overrides);
        this.warnings = this.__initWarnings(this.fieldDefs);

        this.engine = new Engine(rules || [], { allowUndefinedFacts: true });
        this.engine.on('success', this.__ruleSuccess.bind(this));
        this.engine.on('failure', this.__ruleFailure.bind(this));

        this.actions = actions || [];
        this.specialRules = {};

        this.lastEvents = {};
        this.updateCallback = null;
        //magic number used to keep init value different from post rule execute reset
        this.version = 8675309;
        this.renderIndex = 0;
        this.percentComplete = this.__percentComplete();

        this.disabledTabs = this.__initDisabledTabs(layout);
        this.rulesCausedStateChange = false;

        /*
        A2-1931: added this flag so we have an absolute point in time we know
            when rules have completed.  Helpful to know when to render UI.
        */
        this.initComplete = false;
        //console.log(`FormEngine-ctor: this.initComplete: ${this.initComplete} - version: ${this.version}`)
    }

    //func is of the form f(value, ctx)
    createSpecialRule(dataKey, func) {
        this.specialRules[dataKey] = func;
    }

    //PUBLIC MEMBERS
    //Builds an object to matching the API requirements to save answers
    buildSaveAnswers() {
        var answers = this.fieldDefs.map(field => {
            //get version of data with blanked out hidden fields so no data gets accidentally saved that shouldn't be saved
            var blankedFormData = this.__blankDataForHiddenFields(this.formData, this.fieldState);

            //format certain types of answers
            var answer = blankedFormData[field.dataKey];
            if (typeof answer === 'number') answer = `${answer}`;
            if (answer instanceof Date) answer = convertDateToString(answer);

            //sanitize text
            if (field.type !== 'textarea' && typeof answer === 'string') answer = cleanNonSpaceWhiteSpace(answer);

            return {
                QuestionId: field.questionId,
                IsSensitive: field.sensitive ? true : false,
                DataName: field.dataKey,
                IsArray: this.__fieldIsArray(field.dataKey),
                Answer: answer
            }
        });

        if (this.applicationDetails.isReenrollment) {
            var reenrollmentAnswer = answers.find(a => a.DataName === 'Is Reenrollment');
            reenrollmentAnswer.Answer = '1';
        }

        return answers.filter(a => a.Answer);
    }

    //Called by form controls when they change their data.
    //Does two things (change data and warning)... which stinks, but allows us to only
    //invoke a single rerender each time.
    changeData(key, value, warning, previousValue = undefined) { //return Promise (mostly for testing)

        //Nothing has changed, so avoid running rules/update/rerender
        if (value === this.formData[key] && warning === this.warnings[key]) return;

        if (this.__fieldIsAttest(key)) {
            this.__toggleAttestation(key);
        } else {
            this.formData[key] = value;
        }

        this.warnings[key] = warning;

        //specialRules can change formData, fieldState by
        //using the methods specialRuleChangeData and specialRulePerformAction and revertValueToInit
        var specialRule = this.specialRules[key];
        if (specialRule) specialRule(value, this, previousValue);

        return this.__runRules(this.formData);
        //update happens once runRules resolves
    }

    completedTabs() {
        const result = allTabs.map(
            tab => this.__tabComplete(tab.key, this.layout)
                ? tab.key
                : null)
            .filter(x => x !== null);

        return result;
    }

    getFieldDef(key) {
        return this.fieldDefs.filter(def => def.dataKey === key)[0];
    }

    //Calculating the 'required' warning on the fly allows me to avoid quite
    //a bit of code to set/unset required on each value change. The cost of this
    //is we have to call these functions to get the actual warning as opposed to ctx.warnings[field]
    getWarning(def) {
        if (!def) return '';
        //returns either default required warning, or the warning set by the field validation
        const key = def.dataKey;
        // skip required validation for markdown

        if (def.type === 'markdown') return this.warnings[key];

        if (!dataMissing(this.formData[key], this.fieldState[key], def)) return this.warnings[key];

        if (def.type === 'genentechHubPharmacy') return 'All fields are required';

        return def.label ? `"${def.label}" is a required field` : "This is a required field.";
    }

    getWarningByKey(key) {
        const def = this.fieldDefs.filter(fd => fd.dataKey === key)[0];
        if (!def) return '';
        return this.getWarning(def);
    }

    //Tells this component what to call when it is ready to update the screen.
    initCallback(updateCallback) {
        if (this.updateCallback) return;
        this.updateCallback = updateCallback;
    }

    //Called by DigitalForm.js to run the rules in useEffect(). Runs the rules on start (mostly to hide fields).
    initRules() {
        var resetVersion = true;
        const result = this.__runRules(this.formData, resetVersion);

        // A2-1931: set this flag one time - Digital Form render uses this to show form
        if (!this.initComplete) {
            this.initComplete = true;
            //console.log(`FormEngine-initRules().initComplete: ${this.initComplete}`)
        }
        return result;
    }

    loadMeta(key, value) {
        this.meta[key] = value;
    }

    //Ideally, this should never be called.
    //But for some reason, when there are changes in the form and the back button is clicked, the version number is reset even though there are updates.
    //I believe this is some back button/re-rendering goofiness, but I've been in this issue too long to justify figuring that out completely. This works, so there you go.
    overrideVersion(version) {
        this.version = version;
    }

    //Invoked by the caller to get the "output" of the form.
    //Not sure what this shape needs to be. I am speculating something like
    //{questionId, dataName, answer}? I need to look at what the existing
    //form code writes to the db to save answers.
    report() {
        const result = {};

        this.fieldDefs.forEach(fd => {

            if (this.fieldState[fd.dataKey]?.hidden === true) return;

            result[fd.dataKey] = fd.type === 'date'
                //? formatDate(this.formData[fd.dataKey])
                ? getDayjs(this.formData[fd.dataKey]).format("MM/DD/YYYY")
                : this.formData[fd.dataKey]
        });

        return result;
    }

    requiredTabs() {
        const result = allTabs.map(
            tab => this.__tabRequired(tab.key, this.layout)
                ? tab.key
                : null)
            .filter(x => x !== null);

        return result;
    }

    returnValueToInit(key) {
        const myDef = this.fieldDefs.filter(def => def.dataKey === key)[0];
        this.formData[key] = this.__defaultButAllowZeroAndBlank(this.initData[key], this.autofill[key], myDef && myDef.defaultValue);
    }

    //This should only be used by special-case business rules
    //that need to change data. See changeData above.
    specialRuleChangeData(key, value, warning) {
        if (this.__fieldIsArray(key)) {
            if (!value) {
                this.formData[key] = [];
            } else {
                this.__toggleValue(key, value);
            }
        } else if (this.__fieldIsAttest(key)) {
            this.__toggleAttestation(key);
        } else {
            this.formData[key] = value ?? '';
        }

        this.warnings[key] = warning;
    }

    //decided for now to go with
    //{action: 'hide' || 'show' || 'require' || 'unrequire' || 'readonly' || 'readwrite', fields:[]}
    specialRulePerformAction(action, fields) {
        this.__processAction({ func: action, params: fields });
    }

    specialRuleChangeWarning(key, warning) {
        this.warnings[key] = warning;
    }

    __allowTooLongInput(typ) {
        return typ === 'text' || typ === 'textarea';
    }

    //PRIVATE MEMBERS
    __blankDataForHiddenFields(data, fs) {
        //is blanking the fields enough, or should we make it some
        //non-matchable value??
        //IMPORTANT: if the field is a checklist, need to set to empty array instead of null
        //           because the "contains/doesNotContain" operator requires an array.
        const result = { ...data };

        const exceptionKeyList = [
            "Signature Prescriber Key",
            "Signature Patient Disclosure Key",
            "Is Reenrollment", //For reenrollment -- Also, this may not be necessary since we blank before adding the Is Reenrollment answer
            "Patient Is Over 18 AbbVie",
            "Patient Is Over 18 Genentech Copay",
            "Patient Phone Home Spectrum",
            "Patient Phone Cell Spectrum",
            "Patient Phone Work Spectrum"
        ];

        this.fieldDefs.forEach(fd => {
            const key = fd.dataKey;
            if (fs[key]?.hidden === true && !exceptionKeyList.includes(key)) {
                if (fd.type === "checklist") {
                    result[key] = [];
                }
                else {
                    result[key] = null;
                }
            }
        });

        return result;
    }

    __changeHidden(fields, hidden) {
        const fs = { ...this.fieldState };
        fields.forEach(fh => fs[fh] = { ...fs[fh], hidden });
        this.fieldState = fs;
    }

    __changeReadOnly(fields, readonly) {
        const fs = { ...this.fieldState };
        fields.forEach(fh => fs[fh] = { ...fs[fh], readonly });
        this.fieldState = fs;
    }

    __changeRequired(fields, required) {
        const fs = { ...this.fieldState };
        fields.forEach(fh => fs[fh] = { ...fs[fh], required });
        this.fieldState = fs;
    }

    __defaultButAllowZeroAndBlank(dataValue, autoValue, defaultValue) {
        return dataValue !== null && dataValue !== undefined
            ? dataValue
            : autoValue !== null && autoValue !== undefined
                ? autoValue
                : defaultValue !== null && defaultValue !== undefined
                    ? defaultValue
                    : '';
    }

    __fieldIsArray(key) {
        const field = this.getFieldDef(key);

        if (!field) {
            console.log(`${key} has no associated field`);
            return false;
        }

        if (!field.type) {
            console.log(`${key} has a field, but no field type`);
            return false;
        }

        //custom multi-select checklist names need to be added here
        return field.type === "checklist" || field.type === "amgendrugchecklist" || field.type === "panmedications" ||
            Array.isArray(field.defaultValue);
    }

    __fieldIsAttest(key) {
        return this.getFieldDef(key).type === "attest"
    }

    __initDisabledTabs(layout) {
        const usedTabs = distinct(layout, l => l.tab);
        return allTabs.map(t => t.key).filter(tab => !usedTabs.includes(tab));
    }

    __initFieldState(defs) {
        const s = {};
        defs.forEach(f => s[f.dataKey] = { hidden: f.hidden, required: f.required, readonly: f.readonly });
        if (this.applicationDetails.isReenrollment) {
            s['Is Reenrollment'].hidden = true;
        }
        return s;
    }

    __initFormData(defs, data, auto, overrides) {
        const s = {};
        defs.forEach(f => {
            if (this.__fieldIsArray(f.dataKey)) {
                if (data[f.dataKey]?.length > 0) { //need to parse out the actual results only
                    if (Array.isArray(data[f.dataKey])) {
                        data[f.dataKey] = data[f.dataKey].map(d => d);
                    }
                    else if (data[f.dataKey] && (typeof data[f.dataKey]) === 'string') {  //This is necessary because sometimes when only one option is checked for a checklist, it doesn't come back as an array
                        data[f.dataKey] = [data[f.dataKey]];
                    }
                }
                if (auto[f.dataKey]?.length > 0) {
                    if (Array.isArray(auto[f.dataKey])) {
                        auto[f.dataKey] = auto[f.dataKey].map(d => d);
                    }
                    else if (auto[f.dataKey] && (typeof auto[f.dataKey]) === 'string') {  //This is necessary because sometimes when only one option is checked for a checklist, it doesn't come back as an array
                        auto[f.dataKey] = [auto[f.dataKey]];
                    }
                }
            }
            s[f.dataKey] = this.__defaultButAllowZeroAndBlank(data[f.dataKey], auto[f.dataKey], f.defaultValue);
            if (f.maxLength && s[f.dataKey]?.length > f.maxLength && !this.__allowTooLongInput(f.type)) {
                if (f.type === 'phone' || f.type === 'usphone') { //phone numbers may have bad characters like ( ) - so we need to remove these before we truncate
                    s[f.dataKey] = stripNonNumeric(s[f.dataKey]);
                }
                s[f.dataKey] = s[f.dataKey]?.toString().substring(0, f.maxLength);
            }
        });
        if (!checkIfObjectIsEmpty(overrides)) {
            overrides.forEach(o => {
                if (defs.findIndex(d => d.dataKey === o.dataKey) !== -1) {
                    s[o.dataKey] = o.value;
                }
            })
        }
        //custom check for Is Reenrollment since it should exist in practice.FormAnswer but not necessarily in the Form Definition or FormQuestion table
        if (data['Is Reenrollment'] === '1') this.applicationDetails.isReenrollment = true;

        return s;
    }

    __initWarnings(defs) {
        const w = {};
        defs.forEach(f => {
            if (this.__allowTooLongInput(f.type) && f.maxLength && this.formData[f.dataKey].length > f.maxLength) { //Apparenlty, the database stores certain values that are too long, and autofill will just put them in there.
                w[f.dataKey] = `"${f.label}" must be ${f.maxLength} characters or less`
            }
            else w[f.dataKey] = ''
        });
        return w;
    }

    async __innerRunRules(data) {
        //set in the __runSuccess/Failure events
        //If true, an event fired this run that potentially changed something on the form.
        //Thus, the rules will have to be rerun with the new fieldState -- i.e. you are not "done"
        //(see __runRules)
        this.rulesCausedStateChange = false;
        await this.engine.run(data);
        return !this.rulesCausedStateChange;
    }

    __isValid(key) {
        const def = this.getFieldDef(key);

        //escape if we don't find the field...?
        if (def === undefined) return true;

        if (def.type === 'markdown') return true; //markdown fields don't take input, so they can't count
        if (!this.fieldState[key]) return true; //this field isn't on this form, so it doesn't count
        if (this.fieldState[key]?.hidden === true) return true; //this field is hidden and thus does not count

        return !this.getWarning(def); //returns '', or a required warning, or the warning set by the control to indicate it is not valid
    }

    __noUndefinedProps(obj) {
        //null or undefined props become ''
        const result = {};
        for (const prop in obj) {

            const newValue = this.__fieldIsArray(prop) ? [] : ''

            //because zeroes resolve as false, let's make a special case for them
            result[prop] = obj[prop] === 0
                ? 0
                : obj[prop] || newValue;
        }

        return result;
    }


    __percentComplete() {
        const numberOfRequiredFields = this.fieldDefs.reduce((total, field) => {
            if (this.fieldState[field.dataKey].required && this.fieldState[field.dataKey]?.hidden !== true) return total + 1;
            else return total;
        }, 0);
        const numberOfCompletedRequiredFields = this.fieldDefs.reduce((total, field) => {
            if (this.fieldState[field.dataKey].required && this.fieldState[field.dataKey]?.hidden !== true && this.__isValid(field.dataKey)) return total + 1;
            else return total;
        }, 0);
        return numberOfRequiredFields ? (numberOfCompletedRequiredFields / numberOfRequiredFields) * 100 : 0;
    }

    __processAction(action) {
        switch (action.func) {
            case 'hide':
                this.__changeHidden(action.params, 1);
                break;

            case 'show':
                this.__changeHidden(action.params, 0);
                break;

            case 'require':
                this.__changeRequired(action.params, 1);
                break;

            case 'unrequire':
                this.__changeRequired(action.params, 0);
                break;

            case 'readonly':
                this.__changeReadOnly(action.params, 1);
                break;

            case 'readwrite':
                this.__changeReadOnly(action.params, 0);
                break;

            default:
                return;
        }
    }

    //Prep the formData, then run the rules to alter the fieldState (required/hidden). Do this multiple
    //times until you get a run where NOTHING changed. Then stop running rules, and invoke an update
    //in Form.js

    //Blanking the hidden fields allows us to handle dependency chains in our fields
    //(i.e. field1 hides field2 hides field3...), because as a field is hidden in run #1, it's
    //value in run #2 will now be considered to be '', and it will thus fail its rule, and hide its
    //dependent fields, who in run #3, will have their data blanked so they fail their rules... etc.
    async __runRules(data, resetVersion) {
        //Blanking lastEvents each run is a lot chattier with events, but it ensures that every rule runs at least once a run.
        //This is required to match AP1 behavior, where every rule runs each change.
        this.lastEvents = {};
        const engineFriendlyData = this.__noUndefinedProps(data);
        let done = false;

        while (!done) {
            done = await this.__innerRunRules(this.__blankDataForHiddenFields(engineFriendlyData, this.fieldState));
        }

        this.percentComplete = this.__percentComplete(); //Re-calculate the percentage after rules have run: maybe new required fields are now not hidden

        //Talked to Josh, this has something to do with the desired back button behavior
        if (resetVersion) {
            this.version = 0;
            //This wasn't updating before?
            //return this.version;
        }
        return this.__update();
    }

    //These guys are called synchronously by the json-rules-engine as rules succeed/fail.
    //We track the last result (succ || fail) of any particular rule so we avoid needlessly
    //running actions. We also track whether or not ANY actions were invoked as the rules ran
    //i.e. rulesCausedStateChange
    __ruleFailure(evt) {
        if (this.lastEvents[evt.type] === 'failure') return;

        this.rulesCausedStateChange = true;
        this.lastEvents[evt.type] = 'failure';
        const runActions = this.actions.filter(action => action.event === evt.type + '/failure');
        runActions.forEach(action => this.__processAction(action));
    }

    __ruleSuccess(evt) {
        if (this.lastEvents[evt.type] === 'success') return;

        this.rulesCausedStateChange = true;
        this.lastEvents[evt.type] = 'success';
        const runActions = this.actions.filter(action => action.event === evt.type + '/success');
        runActions.forEach(action => this.__processAction(action));
    }

    __tabComplete(tab, layout) {
        const sections = layout.filter(s => s.tab === tab);
        const fields = sections.flatMap(x => x.fields);
        return fields.every(field => this.__isValid(field));
    }

    __tabRequired(tab, layout) {
        const sections = layout.filter(s => s.tab === tab);
        const sectionFields = sections.flatMap(x => x.fields);
        const fields = this.fieldDefs.filter(fd => sectionFields.includes(fd.dataKey))
        return fields.some(field => field.required);
    }

    __toggleAttestation(key) {
        //This is a little wonky, but I need to match the current logic for fear of breaking integrations
        //Is the value 'True' or any non-blank value?
        if (this.formData[key] === 'True') {
            this.formData[key] = '';
        } else {
            this.formData[key] = 'True';
        }
    }

    __toggleValue(key, value) {
        const inputData = this.formData[key] || [];
        let outputData = [];

        if (inputData.includes(value)) {
            outputData = inputData.filter(x => x !== value);
        } else {
            outputData = [...inputData, value];
        }

        this.formData[key] = outputData;
    }

    __update(resetVersion = false) {
        if (resetVersion) {
            this.version = 0;
        }
        else {
            this.version++;
        }
        if (this.updateCallback) this.updateCallback(this.version);
        return this.version;
    }
}

export default FormEngine;
